import { first, type SqlDriver } from './driver';

/**
 * Forward-only schema migrations, tracked in PRAGMA user_version. Each
 * migration runs in one transaction together with its version bump, so a
 * crash mid-migration leaves the previous schema intact.
 * Never edit a shipped migration; add a new one.
 */
export interface Migration {
  version: number;
  name: string;
  statements: string[];
  /**
   * The migration rebuilds a table other tables point at (SQLite's 12-step
   * "create new, copy, drop, rename", the only way to change a CHECK):
   * foreign keys are switched off around it (a pragma that only works
   * outside a transaction) and fully re-checked before they come back on.
   */
  rebuildsReferencedTable?: boolean;
}

/** Bookkeeping columns every synced table carries (see src/sync/types.ts). */
const SYNCED = `
  created_at  INTEGER NOT NULL,
  updated_hlc TEXT NOT NULL,
  deleted_at  INTEGER`;

const DATE = (col: string) => `(${col} IS NULL OR ${col} GLOB '[0-9][0-9][0-9][0-9]-[0-1][0-9]-[0-3][0-9]')`;

export const MIGRATIONS: readonly Migration[] = [
  {
    version: 1,
    name: 'app_meta',
    statements: [
      `CREATE TABLE app_meta (
         key   TEXT PRIMARY KEY NOT NULL,
         value TEXT NOT NULL
       )`,
    ],
  },
  {
    version: 2,
    name: 'core_data_model',
    statements: [
      `CREATE TABLE users (
         id           TEXT PRIMARY KEY NOT NULL,
         display_name TEXT NOT NULL,
         email        TEXT,
         kind         TEXT NOT NULL CHECK (kind IN ('local', 'account')),${SYNCED}
       )`,
      `CREATE TABLE budget_spaces (
         id                   TEXT PRIMARY KEY NOT NULL,
         name                 TEXT NOT NULL CHECK (length(trim(name)) > 0),
         type                 TEXT NOT NULL CHECK (type IN ('personal', 'household')),
         currency             TEXT NOT NULL CHECK (length(currency) = 3),
         billing_country      TEXT CHECK (billing_country IS NULL OR length(billing_country) = 2),
         monthly_target_minor INTEGER CHECK (monthly_target_minor IS NULL OR monthly_target_minor > 0),
         owner_user_id        TEXT NOT NULL REFERENCES users (id),${SYNCED}
       )`,
      `CREATE TABLE budget_members (
         id           TEXT PRIMARY KEY NOT NULL,
         space_id     TEXT NOT NULL REFERENCES budget_spaces (id),
         user_id      TEXT REFERENCES users (id),
         display_name TEXT NOT NULL CHECK (length(trim(display_name)) > 0),
         role         TEXT NOT NULL CHECK (role IN ('owner', 'member')),${SYNCED}
       )`,
      `CREATE UNIQUE INDEX budget_members_one_user ON budget_members (space_id, user_id)
         WHERE user_id IS NOT NULL AND deleted_at IS NULL`,
      `CREATE TABLE categories (
         id          TEXT PRIMARY KEY NOT NULL,
         space_id    TEXT NOT NULL REFERENCES budget_spaces (id),
         system_key  TEXT,
         name        TEXT,
         kind        TEXT NOT NULL CHECK (kind IN ('spending', 'obligation')),
         nature      TEXT CHECK (nature IS NULL OR nature IN ('fixed', 'variable', 'discretionary')),
         color_slot  INTEGER NOT NULL CHECK (color_slot BETWEEN 0 AND 7),
         sort_order  INTEGER NOT NULL,${SYNCED},
         CHECK (system_key IS NOT NULL OR length(trim(name)) > 0)
       )`,
      `CREATE UNIQUE INDEX categories_system_key ON categories (space_id, system_key) WHERE system_key IS NOT NULL`,
      `CREATE TABLE subcategories (
         id           TEXT PRIMARY KEY NOT NULL,
         space_id     TEXT NOT NULL REFERENCES budget_spaces (id),
         category_id  TEXT NOT NULL REFERENCES categories (id),
         system_key   TEXT,
         name         TEXT,
         default_unit TEXT,
         sort_order   INTEGER NOT NULL,${SYNCED},
         CHECK (system_key IS NOT NULL OR length(trim(name)) > 0)
       )`,
      `CREATE INDEX subcategories_category ON subcategories (category_id)`,
      `CREATE TABLE recurring_templates (
         id                 TEXT PRIMARY KEY NOT NULL,
         space_id           TEXT NOT NULL REFERENCES budget_spaces (id),
         category_id        TEXT NOT NULL REFERENCES categories (id),
         subcategory_id     TEXT REFERENCES subcategories (id),
         name               TEXT NOT NULL CHECK (length(trim(name)) > 0),
         kind               TEXT NOT NULL CHECK (kind IN ('fixed', 'variable')),
         type               TEXT NOT NULL CHECK (type IN ('expense', 'debt_payment', 'credit_card_payment', 'transfer', 'refund', 'income')),
         amount_minor       INTEGER CHECK (amount_minor IS NULL OR amount_minor > 0),
         currency           TEXT NOT NULL CHECK (length(currency) = 3),
         frequency          TEXT NOT NULL CHECK (frequency IN ('monthly', 'quarterly', 'yearly')),
         day_of_month       INTEGER CHECK (day_of_month IS NULL OR day_of_month BETWEEN 1 AND 31),
         next_due_date      TEXT CHECK ${DATE('next_due_date')},
         paid_by            TEXT REFERENCES budget_members (id),
         ownership          TEXT NOT NULL CHECK (ownership IN ('shared', 'personal')),
         owner_member_id    TEXT REFERENCES budget_members (id),
         counts_as_spending INTEGER NOT NULL CHECK (counts_as_spending IN (0, 1)),
         active             INTEGER NOT NULL CHECK (active IN (0, 1)),${SYNCED},
         CHECK (kind = 'variable' OR amount_minor IS NOT NULL),
         CHECK ((ownership = 'shared' AND owner_member_id IS NULL) OR (ownership = 'personal' AND owner_member_id IS NOT NULL))
       )`,
      `CREATE TABLE transactions (
         id                    TEXT PRIMARY KEY NOT NULL,
         space_id              TEXT NOT NULL REFERENCES budget_spaces (id),
         category_id           TEXT NOT NULL REFERENCES categories (id),
         subcategory_id        TEXT REFERENCES subcategories (id),
         type                  TEXT NOT NULL CHECK (type IN ('expense', 'debt_payment', 'credit_card_payment', 'transfer', 'refund', 'income')),
         amount_minor          INTEGER NOT NULL CHECK (amount_minor > 0),
         currency              TEXT NOT NULL CHECK (length(currency) = 3),
         date                  TEXT NOT NULL CHECK ${DATE('date')},
         added_by              TEXT NOT NULL REFERENCES budget_members (id),
         paid_by               TEXT NOT NULL REFERENCES budget_members (id),
         ownership             TEXT NOT NULL CHECK (ownership IN ('shared', 'personal')),
         owner_member_id       TEXT REFERENCES budget_members (id),
         counts_as_spending    INTEGER NOT NULL CHECK (counts_as_spending IN (0, 1)),
         notes                 TEXT,
         provider              TEXT,
         due_date              TEXT CHECK ${DATE('due_date')},
         period_start          TEXT CHECK ${DATE('period_start')},
         period_end            TEXT CHECK ${DATE('period_end')},
         recurring_template_id TEXT REFERENCES recurring_templates (id),${SYNCED},
         CHECK ((ownership = 'shared' AND owner_member_id IS NULL) OR (ownership = 'personal' AND owner_member_id IS NOT NULL))
       )`,
      `CREATE INDEX transactions_space_date ON transactions (space_id, date) WHERE deleted_at IS NULL`,
      `CREATE INDEX transactions_space_category_date ON transactions (space_id, category_id, date) WHERE deleted_at IS NULL`,
      `CREATE TABLE consumption_data (
         id             TEXT PRIMARY KEY NOT NULL,
         space_id       TEXT NOT NULL REFERENCES budget_spaces (id),
         transaction_id TEXT NOT NULL REFERENCES transactions (id),
         quantity       REAL NOT NULL CHECK (quantity >= 0),
         unit           TEXT NOT NULL CHECK (length(trim(unit)) > 0),
         period_start   TEXT CHECK ${DATE('period_start')},
         period_end     TEXT CHECK ${DATE('period_end')},${SYNCED}
       )`,
      `CREATE UNIQUE INDEX consumption_one_per_transaction ON consumption_data (transaction_id) WHERE deleted_at IS NULL`,
      `CREATE TABLE activity_entries (
         id              TEXT PRIMARY KEY NOT NULL,
         space_id        TEXT NOT NULL REFERENCES budget_spaces (id),
         actor_member_id TEXT REFERENCES budget_members (id),
         action          TEXT NOT NULL CHECK (action IN ('created', 'updated', 'deleted', 'restored', 'marked_paid')),
         entity          TEXT NOT NULL,
         entity_id       TEXT NOT NULL,
         summary         TEXT NOT NULL,
         at              INTEGER NOT NULL,${SYNCED}
       )`,
      `CREATE INDEX activity_space_at ON activity_entries (space_id, at)`,
      `CREATE TABLE sync_outbox (
         seq        INTEGER PRIMARY KEY AUTOINCREMENT,
         table_name TEXT NOT NULL,
         row_id     TEXT NOT NULL,
         space_id   TEXT,
         op         TEXT NOT NULL CHECK (op IN ('upsert', 'delete')),
         hlc        TEXT NOT NULL,
         fields     TEXT NOT NULL,
         created_at INTEGER NOT NULL
       )`,
    ],
  },
  {
    version: 3,
    name: 'category_archive',
    // Archive is not delete: archived categories leave the pickers but keep
    // labelling their history, and come back with unarchive.
    statements: [
      `ALTER TABLE categories ADD COLUMN archived_at INTEGER`,
      `ALTER TABLE subcategories ADD COLUMN archived_at INTEGER`,
    ],
  },
  {
    version: 4,
    name: 'debt_details',
    // Optional state of a loan or card at a payment (brief §13). The bank is
    // the entry's provider and the due date the entry's due date.
    statements: [
      `CREATE TABLE debt_details (
         id                      TEXT PRIMARY KEY NOT NULL,
         space_id                TEXT NOT NULL REFERENCES budget_spaces (id),
         transaction_id          TEXT NOT NULL REFERENCES transactions (id),
         remaining_balance_minor INTEGER CHECK (remaining_balance_minor IS NULL OR remaining_balance_minor >= 0),
         interest_rate_bp        INTEGER CHECK (interest_rate_bp IS NULL OR interest_rate_bp BETWEEN 0 AND 100000),
         installments_left       INTEGER CHECK (installments_left IS NULL OR installments_left BETWEEN 0 AND 1200),${SYNCED},
         CHECK (remaining_balance_minor IS NOT NULL OR interest_rate_bp IS NOT NULL OR installments_left IS NOT NULL)
       )`,
      `CREATE UNIQUE INDEX debt_one_per_transaction ON debt_details (transaction_id) WHERE deleted_at IS NULL`,
    ],
  },
  {
    version: 5,
    name: 'budget_type_other',
    // Budgets beyond Personal and Home (a holiday, a flat abroad): a third
    // type. Rows keep their ids, so every table pointing at a budget still does.
    rebuildsReferencedTable: true,
    statements: [
      `CREATE TABLE budget_spaces_v5 (
         id                   TEXT PRIMARY KEY NOT NULL,
         name                 TEXT NOT NULL CHECK (length(trim(name)) > 0),
         type                 TEXT NOT NULL CHECK (type IN ('personal', 'household', 'other')),
         currency             TEXT NOT NULL CHECK (length(currency) = 3),
         billing_country      TEXT CHECK (billing_country IS NULL OR length(billing_country) = 2),
         monthly_target_minor INTEGER CHECK (monthly_target_minor IS NULL OR monthly_target_minor > 0),
         owner_user_id        TEXT NOT NULL REFERENCES users (id),${SYNCED}
       )`,
      `INSERT INTO budget_spaces_v5 (id, name, type, currency, billing_country, monthly_target_minor, owner_user_id, created_at, updated_hlc, deleted_at)
         SELECT id, name, type, currency, billing_country, monthly_target_minor, owner_user_id, created_at, updated_hlc, deleted_at FROM budget_spaces`,
      `DROP TABLE budget_spaces`,
      `ALTER TABLE budget_spaces_v5 RENAME TO budget_spaces`,
    ],
  },
  {
    version: 6,
    name: 'shared_conflicts',
    // Phase 20. Each outbox write carries the values it replaced ("prev"), so
    // the server can tell a concurrent edit (someone else changed the field
    // meanwhile) from an ordinary one; and the activity log can hold the
    // conflicts it finds. activity_entries is rebuilt to widen its CHECK;
    // no table points at it.
    statements: [
      `ALTER TABLE sync_outbox ADD COLUMN prev TEXT`,
      `CREATE TABLE activity_entries_v6 (
         id              TEXT PRIMARY KEY NOT NULL,
         space_id        TEXT NOT NULL REFERENCES budget_spaces (id),
         actor_member_id TEXT REFERENCES budget_members (id),
         action          TEXT NOT NULL CHECK (action IN ('created', 'updated', 'deleted', 'restored', 'marked_paid', 'conflict')),
         entity          TEXT NOT NULL,
         entity_id       TEXT NOT NULL,
         summary         TEXT NOT NULL,
         at              INTEGER NOT NULL,${SYNCED}
       )`,
      `INSERT INTO activity_entries_v6 (id, space_id, actor_member_id, action, entity, entity_id, summary, at, created_at, updated_hlc, deleted_at)
         SELECT id, space_id, actor_member_id, action, entity, entity_id, summary, at, created_at, updated_hlc, deleted_at FROM activity_entries`,
      `DROP TABLE activity_entries`,
      `ALTER TABLE activity_entries_v6 RENAME TO activity_entries`,
      `CREATE INDEX activity_space_at ON activity_entries (space_id, at)`,
    ],
  },
  {
    version: 7,
    name: 'entry_type_index',
    // Phase 26: "how was this card's last bill counted?" read every entry of
    // the budget (0.9 s with 10,000 on a phone); by type it is a lookup.
    statements: [`CREATE INDEX transactions_space_type_date ON transactions (space_id, type, date) WHERE deleted_at IS NULL`],
  },
  {
    version: 8,
    name: 'entry_pick_index',
    // Phase 26: the latest entry of each category/subcategory ("Same as last
    // time", Quick Add's warm cache) read the whole budget at start (1.3 s
    // with 10,000 on a phone, holding every other query behind it); with this
    // index each one is a single lookup.
    statements: [`CREATE INDEX transactions_pick_date ON transactions (space_id, category_id, subcategory_id, date) WHERE deleted_at IS NULL`],
  },
  {
    version: 9,
    name: 'entry_original_currency',
    // Paid in another currency: amount_minor stays in the budget's currency
    // (every total adds it as before); what was actually paid is kept here.
    statements: [
      `ALTER TABLE transactions ADD COLUMN original_amount_minor INTEGER CHECK (original_amount_minor IS NULL OR original_amount_minor > 0)`,
      `ALTER TABLE transactions ADD COLUMN original_currency TEXT`,
    ],
  },
];

export async function schemaVersion(db: SqlDriver): Promise<number> {
  const row = await first<{ user_version: number }>(db, 'PRAGMA user_version');
  return Number(row?.user_version ?? 0);
}

export async function migrate(db: SqlDriver, migrations: readonly Migration[] = MIGRATIONS): Promise<number> {
  const sorted = [...migrations].sort((a, b) => a.version - b.version);
  sorted.forEach((m, i) => {
    if (m.version !== i + 1) throw new Error(`Migration versions must be contiguous from 1 (got ${m.version} at index ${i})`);
  });
  let current = await schemaVersion(db);
  for (const m of sorted) {
    if (m.version <= current) continue;
    if (m.rebuildsReferencedTable) await db.run('PRAGMA foreign_keys = OFF');
    try {
      await db.batch([...m.statements.map((sql) => ({ sql })), { sql: `PRAGMA user_version = ${m.version}` }]);
      if (m.rebuildsReferencedTable) {
        const broken = await db.all('PRAGMA foreign_key_check');
        if (broken.length) throw new Error(`Migration ${m.version} left ${broken.length} broken references`);
      }
    } finally {
      if (m.rebuildsReferencedTable) await db.run('PRAGMA foreign_keys = ON');
    }
    current = m.version;
  }
  return current;
}
